'use client';

import { FC } from 'react';

import { CallOffOrderDetail, MappedOrders, OrderBaseUrls } from '@ravago/shared/page-data/models/elements';
import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { Customer } from '@ravago/shared/page-data/models/features';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { DateUtils } from '@ravago/shared/page-data/utils/date';
import { Alert } from '@ravago/shared/radiance/components/alert/components/Alert/Alert';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { CallOffProgressBar } from '../../../shared/order/call-off-progress-bar';
import { OrderDetailsOrderLines } from '../../../shared/order/order-details-order-lines';
import { OrderShipments } from '../../../shared/order/order-shipments';

import { CallOffOrderDeliveryRequestPaper } from './call-off-order-delivery-request-paper';
import { CallOffOrderInfo } from './call-off-order-info';
import { CallOffOrderPageHeader } from './call-off-order-page-header';

export type Props = {
   globals: RendererGlobals;
   callOffOrderDetail: CallOffOrderDetail;
   consumingOrders: MappedOrders | undefined;
   customer: Customer;
   orderBaseUrls: OrderBaseUrls;
   deliveryAddress: Address | undefined;
   isUSBusinessContext: boolean;
   usesAgreementApp: boolean;
};
export const CallOffOrderDetails: FC<Props> = ({
   globals: {
      locale,
      acceptWebp,
      isConsumer,
      actions: { requestBol, requestCoa, requestDeliveryNote, downloadDocument }
   },
   callOffOrderDetail,
   consumingOrders,
   customer,
   orderBaseUrls,
   deliveryAddress,
   isUSBusinessContext,
   usesAgreementApp
}) => {
   const translate = useTranslations();
   const format = useFormatter();

   return (
      <Box direction="column" gap="xl">
         {callOffOrderDetail.status === 'Call Off Overdue' && (
            <Alert variant="warning" headline={translate('my-orders.agreement-overdue')}>
               <Typography component="span" variant="body">
                  {translate('my-orders.agreement-overdue-message')}
               </Typography>
            </Alert>
         )}
         <CallOffOrderPageHeader
            headerText={translate('my-orders.call-off-agreement-header')}
            id={callOffOrderDetail.id}
            date={DateUtils.transformCallOffOrderPeriod(format, callOffOrderDetail.callOffPeriod)}
         />
         {usesAgreementApp && <CallOffProgressBar callOffOrderDetail={callOffOrderDetail} showLabel showSupportingText />}
         <BreakpointSwitch includeViews={['desktop', 'tab-land']}>
            <Box>
               <Box width="50%">
                  <CallOffOrderInfo
                     locale={locale}
                     callOffOrderDetail={callOffOrderDetail}
                     productsBaseRoute={orderBaseUrls.productBaseUrl}
                     usesAgreementApp={usesAgreementApp}
                  />
               </Box>
               <Box width="50%" height="100%">
                  <CallOffOrderDeliveryRequestPaper callOffOrderDetail={callOffOrderDetail} deliveryAddress={deliveryAddress} customer={customer} />
               </Box>
            </Box>
         </BreakpointSwitch>
         <BreakpointSwitch includeViews={['phone', 'tab-port']}>
            <Box direction="column">
               <CallOffOrderInfo
                  locale={locale}
                  callOffOrderDetail={callOffOrderDetail}
                  productsBaseRoute={orderBaseUrls.productBaseUrl}
                  usesAgreementApp={usesAgreementApp}
               />
               <CallOffOrderDeliveryRequestPaper callOffOrderDetail={callOffOrderDetail} deliveryAddress={deliveryAddress} customer={customer} />
            </Box>
         </BreakpointSwitch>
         {usesAgreementApp && (
            <Box direction="column" gap="md">
               <Typography variant="heading-2" component="h2" align="left" color="strong">
                  {translate('my-orders.agreement-consuming-orders-title')}
               </Typography>
               {consumingOrders &&
               consumingOrders.openOrderLines.length +
                  consumingOrders.closedOrderLines.length +
                  consumingOrders.transportArrangedShipments.length +
                  consumingOrders.inTransitShipments.length >
                  0 ? (
                  <>
                     <OrderShipments
                        isConsumer={isConsumer}
                        locale={locale}
                        acceptWebp={acceptWebp}
                        orderDetail={consumingOrders}
                        openedFrom="call_off_agreement"
                        orderBaseUrls={orderBaseUrls}
                        isUSBusinessContext={isUSBusinessContext}
                        requestBol={requestBol}
                        requestCoa={requestCoa}
                        requestDeliveryNote={requestDeliveryNote}
                        downloadDocument={downloadDocument}
                     />
                     <OrderDetailsOrderLines
                        isConsumer={isConsumer}
                        acceptWebp={acceptWebp}
                        locale={locale}
                        orderDetail={consumingOrders}
                        orderBaseUrls={orderBaseUrls}
                        openedFrom="call_off_agreement"
                        requestCoa={requestCoa}
                        requestBol={requestBol}
                        requestDeliveryNote={requestDeliveryNote}
                        downloadDocument={downloadDocument}
                        isUSBusinessContext={isUSBusinessContext}
                     />
                  </>
               ) : (
                  <Typography variant="body" component="b" align="center" color="strong">
                     {translate('my-orders.agreement-consuming-orders-empty-message')}
                  </Typography>
               )}
            </Box>
         )}
      </Box>
   );
};
